import type { MutableRefObject, RefObject } from "react";
import type { PlayerVideoHandle } from "../components/playerVideoTypes";
import type { Credentials } from "../storage/credentials";
import type { PlaybackLifecycleEvent } from "./lifecycle";
import type { PlaybackMethod } from "./streamPlan";
import { usePlayerSeek } from "./usePlayerSeek";
import { usePlayerSession } from "./usePlayerSession";

type PlayerTransportRuntimeInput = {
  credentials: Credentials | null;
  itemId: string;
  mediaSourceId?: string;
  playSessionId?: string;
  playMethod: PlaybackMethod;
  timelineDuration: number;
  decoderOffsetSeconds: number;
  positionRef: MutableRefObject<number>;
  pausedRef: MutableRefObject<boolean>;
  mutedRef: MutableRefObject<boolean>;
  startedRef: MutableRefObject<boolean>;
  videoRef: RefObject<PlayerVideoHandle>;
  dispatchLifecycle: (event: PlaybackLifecycleEvent) => void;
  markProgressReported: (seconds: number) => void;
  onSeekTimeout: () => void;
  onActivity: () => void;
};

/** Owns the serialized server session and authoritative seek transaction. */
export function usePlayerTransportRuntime(input: PlayerTransportRuntimeInput) {
  const session = usePlayerSession({
    credentials: input.credentials,
    itemId: input.itemId,
    mediaSourceId: input.mediaSourceId,
    playSessionId: input.playSessionId,
    playMethod: input.playMethod,
    positionRef: input.positionRef,
    pausedRef: input.pausedRef,
    mutedRef: input.mutedRef,
    startedRef: input.startedRef,
  });
  const seek = usePlayerSeek({
    durationSeconds: input.timelineDuration,
    decoderOffsetSeconds: input.decoderOffsetSeconds,
    positionRef: input.positionRef,
    videoRef: input.videoRef,
    onPosition: (seconds) => {
      input.markProgressReported(seconds);
      input.dispatchLifecycle({ type: "SET_POSITION", value: seconds });
    },
    onIntent: session.progress,
    onTimeout: input.onSeekTimeout,
    onActivity: input.onActivity,
  });
  return { session, seek };
}
